/**
 * api.js
 * Utilidad centralizada para peticiones HTTP con autenticación JWT
 *
 * Este módulo proporciona funciones helper para hacer peticiones al backend
 * con el token JWT automáticamente incluido en los headers.
 */

// URL del API - siempre relativo porque Nginx proxea /api/ al backend
// tanto en el dominio principal como en los subdominios de colegios
const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Obtiene el token JWT almacenado
 * Busca primero en sessionStorage (para sesiones de impersonation)
 * y luego en localStorage (para sesiones normales)
 * @returns {string|null} Token JWT o null si no existe
 */
export function getToken() {
  // Primero buscar en sessionStorage (sesiones de impersonation, específicas de pestaña)
  const sessionToken = sessionStorage.getItem('token');
  if (sessionToken) {
    return sessionToken;
  }
  // Luego buscar en localStorage (sesiones normales, compartidas entre pestañas)
  return localStorage.getItem('token');
}

/**
 * Guarda el token JWT en localStorage
 * @param {string} token - Token JWT a guardar
 */
export function setToken(token) {
  if (token) {
    localStorage.setItem('token', token);
  }
}

/**
 * Elimina el token JWT de ambos storages
 */
export function removeToken() {
  sessionStorage.removeItem('token');
  localStorage.removeItem('token');
}

/**
 * Construye los headers para una petición HTTP incluyendo el token JWT
 * @param {Object} additionalHeaders - Headers adicionales opcionales
 * @returns {Object} Headers con Content-Type y Authorization
 */
export function getAuthHeaders(additionalHeaders = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  };

  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Maneja la respuesta de una petición, verificando errores de autenticación
 * @param {Response} response - Objeto Response de fetch
 * @returns {Promise<any>} Datos de la respuesta
 * @throws {Error} Si la respuesta no es OK
 */
async function handleResponse(response) {
  // Si el token expiró o es inválido
  if (response.status === 401) {
    // Limpiar sesión de ambos storages
    removeToken();
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('isImpersonated');
    localStorage.removeItem('user');

    // Redirigir al login
    window.location.replace('/');

    throw new Error('Sesión expirada. Por favor inicie sesión nuevamente.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const err = new Error(errorData.error || errorData.message || `Error ${response.status}`);
    err.status = response.status;
    err.data = errorData;
    throw err;
  }

  // Verificar si hay contenido para parsear
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
}

/**
 * Realiza una petición GET autenticada
 * @param {string} endpoint - Endpoint relativo (ej: '/usuarios/esquema')
 * @returns {Promise<any>} Datos de la respuesta
 */
export async function get(endpoint) {
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
}

/**
 * Realiza una petición POST autenticada
 * @param {string} endpoint - Endpoint relativo
 * @param {Object} data - Datos a enviar en el body
 * @returns {Promise<any>} Datos de la respuesta
 */
export async function post(endpoint, data) {
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  return handleResponse(response);
}

/**
 * Realiza una petición PUT autenticada
 * @param {string} endpoint - Endpoint relativo
 * @param {Object} data - Datos a enviar en el body
 * @returns {Promise<any>} Datos de la respuesta
 */
export async function put(endpoint, data) {
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  return handleResponse(response);
}

/**
 * Realiza una petición DELETE autenticada
 * @param {string} endpoint - Endpoint relativo
 * @param {Object} data - Datos opcionales a enviar en el body
 * @returns {Promise<any>} Datos de la respuesta
 */
export async function del(endpoint, data = null) {
  const options = {
    method: 'DELETE',
    headers: getAuthHeaders()
  };

  if (data) {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(`${API_URL}${endpoint}`, options);

  return handleResponse(response);
}

/**
 * Realiza una petición POST con FormData (para subir archivos)
 * @param {string} endpoint - Endpoint relativo
 * @param {FormData} formData - FormData con archivos
 * @returns {Promise<any>} Datos de la respuesta
 */
export async function postFormData(endpoint, formData) {
  const token = getToken();
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // No incluir Content-Type, fetch lo agrega automáticamente para FormData

  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData
  });

  return handleResponse(response);
}

/**
 * Descarga un archivo autenticado (para backups)
 * @param {string} endpoint - Endpoint relativo
 * @returns {Promise<{blob: Blob, filename: string}>} Blob del archivo y nombre
 */
export async function downloadFile(endpoint) {
  const token = getToken();
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'GET',
    headers
  });

  if (response.status === 401) {
    removeToken();
    localStorage.removeItem('user');
    window.location.replace('/');
    throw new Error('Sesión expirada');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Error al descargar archivo');
  }

  // Obtener el nombre del archivo del header Content-Disposition
  const contentDisposition = response.headers.get('content-disposition');
  let filename = 'backup.sql';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();
  return { blob, filename };
}

/**
 * Genera una URL autenticada para recursos estáticos protegidos (imágenes, archivos)
 * Agrega el token JWT como query param para que el middleware autenticar lo valide
 * @param {string} path - Ruta del recurso (ej: '/api/uploads/logos/logo.png')
 * @returns {string} URL con token de autenticación
 */
export function getAuthUrl(path) {
  if (!path) return '';
  const token = getToken();
  if (!token) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}token=${token}`;
}

// Exportar también la URL base por si es necesaria
export { API_URL };

// Objeto con todos los métodos para importación conveniente
const api = {
  get,
  post,
  put,
  del,
  delete: del, // Alias
  postFormData,
  upload: postFormData, // Alias para subida de archivos
  downloadFile,
  getToken,
  setToken,
  removeToken,
  getAuthHeaders,
  getAuthUrl,
  API_URL
};

export default api;
